import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "nsp_pwa_dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 giorni

/* Prompt di installazione della PWA — si mostra dopo alcuni secondi
   solo se il browser supporta l'evento beforeinstallprompt e l'app
   non è già installata. iOS Safari mostra invece un suggerimento
   testuale (non supporta l'API standard). */
export const PWAInstallPrompt = () => {
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [isIos, setIsIos] = useState(false);

    useEffect(() => {
        // Non mostrare se già installata
        const isStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (isStandalone) return;

        // Non mostrare se dismesso di recente
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_MS) return;

        // Detect iOS
        const ua = navigator.userAgent || "";
        const iosCandidate = /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS/.test(ua);
        setIsIos(iosCandidate);

        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
            // ritardo per non essere invasivo
            setTimeout(() => setShow(true), 4000);
        };
        window.addEventListener("beforeinstallprompt", handler);

        // Su iOS mostra l'hint testuale dopo 8s
        if (iosCandidate) {
            const t = setTimeout(() => setShow(true), 8000);
            return () => {
                window.removeEventListener("beforeinstallprompt", handler);
                clearTimeout(t);
            };
        }
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const install = async () => {
        if (!deferred) return;
        await deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
        setShow(false);
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    const dismiss = () => {
        setShow(false);
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    if (!show) return null;

    return (
        <div
            data-testid="pwa-install-prompt"
            className="fixed left-3 right-3 bottom-20 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-50 bg-nsp-green text-white rounded-2xl shadow-2xl animate-slide-up overflow-hidden"
            role="dialog"
            aria-label="Installa app NSP"
        >
            <div className="p-4 flex items-start gap-3">
                <div className="w-12 h-12 bg-nsp-yellow flex-shrink-0 flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-nsp-green" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg uppercase tracking-tight leading-tight">
                        Installa NSP Noleggio
                    </div>
                    <p className="text-xs text-white/80 mt-1 leading-relaxed">
                        {isIos ? (
                            <>
                                Tocca <b>Condividi</b> nel browser, poi{" "}
                                <b>"Aggiungi a Home"</b> per installare l'app.
                            </>
                        ) : (
                            "Aggiungi l'app alla tua schermata home per un accesso più rapido."
                        )}
                    </p>
                </div>
                <button
                    onClick={dismiss}
                    data-testid="pwa-install-close"
                    className="w-8 h-8 flex items-center justify-center hover:bg-nsp-green-dark rounded-full"
                    aria-label="Chiudi"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            {!isIos && (
                <div className="border-t border-white/10 p-3 flex gap-2">
                    <button
                        onClick={dismiss}
                        data-testid="pwa-install-later"
                        className="flex-1 h-10 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-nsp-yellow"
                    >
                        Più tardi
                    </button>
                    <button
                        onClick={install}
                        data-testid="pwa-install-confirm"
                        className="flex-1 h-10 bg-nsp-yellow text-nsp-green text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-white"
                    >
                        <Download className="w-3.5 h-3.5" /> Installa
                    </button>
                </div>
            )}
        </div>
    );
};

export default PWAInstallPrompt;
